/**
 * What "Open" puts in a new tab for a run artifact (security report F1, 2026-10-13).
 *
 * WHY THIS EXISTS. The artifact endpoints need auth headers a plain link can't send, so "Open" fetches the
 * file and navigates a tab to a `blob:` URL. A blob URL belongs to the page that CREATED it: an HTML or SVG
 * artifact served under its own content type ran as the app — same origin, same cookies (`auth_token`,
 * `refresh_token`), same localStorage — and an agent can be prompt-injected into writing one. So the server's
 * content type is never trusted for a navigation:
 *
 * - passive types (PDF, raster images, text, JSON, CSV) open as themselves;
 * - markup (HTML, SVG, XML) is shown inside a sandboxed iframe with NO `allow-scripts` and NO
 *   `allow-same-origin` — an opaque origin that cannot run script or reach the app — under a CSP that also
 *   blocks remote loads, so a rendered report cannot beacon data out through an `<img src>`;
 * - anything else opens as text/plain (an empty or unknown type would otherwise be content-sniffed).
 *
 * The DOWNLOAD paths are untouched: a saved file keeps its real type.
 */
import { isLwcTemplate } from "@/lib/studio/utils";

export type ArtifactViewKind = "passive" | "sandboxed" | "text";

const PASSIVE_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/bmp",
  "text/plain",
  "text/csv",
  "application/json",
]);

const MARKUP_TYPES = new Set(["text/html", "application/xhtml+xml", "image/svg+xml", "text/xml", "application/xml"]);

/** Artifact endpoints often answer `application/octet-stream`; these extensions are trusted to name a passive type then. */
const PASSIVE_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};

const MARKUP_EXTENSIONS = new Set(["html", "htm", "xhtml", "svg", "xml"]);

function baseType(mime: string): string {
  return mime.split(";")[0].trim().toLowerCase();
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

/**
 * How an artifact is shown. An LWC template (`x.html` beside `x.js-meta.xml`) is SOURCE: rendered it is a
 * blank page (reported 2026-09-17), so it is text. A standalone `.html` is a report export and renders sandboxed.
 */
export function artifactViewKind(mime: string, name: string, allNames: readonly string[]): ArtifactViewKind {
  if (isLwcTemplate(name, [...allNames])) return "text";
  const type = baseType(mime);
  if (PASSIVE_TYPES.has(type)) return "passive";
  if (MARKUP_TYPES.has(type) || MARKUP_EXTENSIONS.has(extensionOf(name))) return "sandboxed";
  if (PASSIVE_BY_EXTENSION[extensionOf(name)] && (type === "" || type === "application/octet-stream")) return "passive";
  return "text";
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * `img-src`/`font-src` stay local (data:/blob:) on purpose: a remote URL in a generated report is a way to carry
 * org data out with no script at all. `srcdoc` frames inherit this policy. Styles may be inline only.
 */
const VIEWER_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; media-src data: blob:";

/**
 * A page that shows `markup` in a sandboxed frame. `allow-popups(-to-escape-sandbox)` keeps a report's
 * `target=_blank` links working; with no `allow-scripts` a `javascript:` link cannot run either.
 */
export function sandboxedViewerHtml(markup: string, title: string): string {
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${VIEWER_CSP}">` +
    `<meta name="referrer" content="no-referrer">` +
    `<title>${escapeHtml(title)}</title>` +
    `<style>html,body{margin:0;height:100%;background:#fff}iframe{display:block;border:0;width:100%;height:100%}</style>` +
    `</head><body><iframe sandbox="allow-popups allow-popups-to-escape-sandbox" referrerpolicy="no-referrer" ` +
    `title="${escapeHtml(title)}" srcdoc="${escapeHtml(markup)}"></iframe></body></html>`
  );
}

/** The blob a new tab may safely navigate to for this artifact. */
export async function artifactViewBlob(blob: Blob, name: string, allNames: readonly string[]): Promise<Blob> {
  switch (artifactViewKind(blob.type, name, allNames)) {
    case "passive": {
      const type = PASSIVE_TYPES.has(baseType(blob.type)) ? blob.type : PASSIVE_BY_EXTENSION[extensionOf(name)];
      return blob.type === type ? blob : blob.slice(0, blob.size, type);
    }
    case "sandboxed":
      return new Blob([sandboxedViewerHtml(await blob.text(), name)], { type: "text/html; charset=utf-8" });
    case "text":
      return blob.slice(0, blob.size, "text/plain; charset=utf-8");
  }
}

/**
 * Open a tab synchronously inside the click (popup-blocker safe) and cut its `opener` before anything loads in it:
 * the tab never gets a live handle back to the app, whatever it ends up showing.
 */
export function openBlankViewerTab(): Window | null {
  const win = window.open("about:blank", "_blank");
  if (win) win.opener = null;
  return win;
}
